import React, { useMemo, useState } from 'react';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const COLORS = ['#2563EB', '#F97316', '#22C55E', '#8B5CF6', '#EF4444', '#0EA5E9', '#F59E0B', '#14B8A6'];
const SVG_HEIGHT = 320;
const MARGIN = { top: 28, right: 28, bottom: 52, left: 54 };
const POINT_RADIUS = 4;

const InquiryYearLineChart = ({ data }) => {
  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    label: '',
    value: 0
  });

  const series = useMemo(() => {
    if (!Array.isArray(data) || !data.length) {
      return {
        years: [],
        maxValue: 0,
        rows: []
      };
    }

    const yearMap = new Map();
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthIdx = now.getMonth();

    data.forEach((item) => {
      const rawMonth = typeof item?.month === 'string' ? item.month.trim() : '';
      if (!rawMonth) return;
      const [monthLabel, yearLabel] = rawMonth.split(' ');
      const monthIdx = MONTH_LABELS.findIndex((label) => label === monthLabel);
      const year = Number(yearLabel);
      if (monthIdx < 0 || Number.isNaN(year)) return;

      if (!yearMap.has(year)) {
        yearMap.set(year, Array.from({ length: 12 }, () => 0));
      }
      const metric = Number(item?.numberOfStudentInquiries ?? item?.inquiries ?? 0);
      yearMap.get(year)[monthIdx] = metric;
    });

    const years = Array.from(yearMap.keys()).sort((a, b) => a - b);
    const rows = years.map((year) => ({
      year,
      values: yearMap.get(year).map((value, monthIdx) => {
        if (year === currentYear && monthIdx > currentMonthIdx) {
          return null;
        }
        return value;
      })
    }));

    const maxValue = rows.reduce(
      (max, row) => Math.max(max, ...row.values.map((value) => (value == null ? 0 : value))),
      0
    );

    return { years, maxValue, rows };
  }, [data]);

  if (!series.years.length) {
    return <div className="text-sm text-slate-500">No inquiry data available yet.</div>;
  }

  if (!series.maxValue) {
    return <div className="text-sm text-slate-500">No inquiry data available yet.</div>;
  }

  const chartWidth = 860;
  const innerWidth = chartWidth - MARGIN.left - MARGIN.right;
  const innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const xStep = innerWidth / Math.max(1, MONTH_LABELS.length - 1);
  const latestYear = series.years[series.years.length - 1];
  const oldestYear = series.years[0];
  const yearRange = Math.max(1, latestYear - oldestYear);

  const getX = (monthIdx) => MARGIN.left + monthIdx * xStep;
  const getY = (value) => MARGIN.top + innerHeight - (value / series.maxValue) * innerHeight;
  const getYearOpacity = (year) => {
    if (year === latestYear) return 1;
    const normalizedAge = (latestYear - year) / yearRange;
    return Math.max(0.22, 1 - normalizedAge * 0.6);
  };

  const showTooltip = (x, y, label, value) => {
    setTooltip({ show: true, x, y, label, value });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        {series.years.map((year, idx) => (
          <span key={year} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: COLORS[idx % COLORS.length], opacity: getYearOpacity(year) }}
            />
            <span style={{ opacity: getYearOpacity(year) }}>{year}</span>
          </span>
        ))}
      </div>

      <div className="relative overflow-x-auto">
        <svg
          width={chartWidth}
          height={SVG_HEIGHT}
          role="img"
          aria-label="Inquiries by month with separate lines for each year"
          className="text-xs"
          onMouseLeave={() => setTooltip((prev) => ({ ...prev, show: false }))}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((t, idx) => {
            const value = Math.round(series.maxValue * t);
            const y = MARGIN.top + innerHeight * (1 - t);
            return (
              <g key={idx}>
                <line
                  x1={MARGIN.left}
                  x2={chartWidth - MARGIN.right}
                  y1={y}
                  y2={y}
                  stroke="rgba(148, 163, 184, 0.22)"
                />
                <text
                  x={MARGIN.left - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="#94A3B8"
                >
                  {value.toLocaleString()}
                </text>
              </g>
            );
          })}

          {MONTH_LABELS.map((label, monthIdx) => (
            <text
              key={label}
              x={getX(monthIdx)}
              y={SVG_HEIGHT - MARGIN.bottom + 22}
              textAnchor="middle"
              fill="#475569"
              className="font-medium"
            >
              {label}
            </text>
          ))}

          {series.rows.map((row, rowIdx) => {
            const path = row.values
              .map((value, monthIdx) => {
                if (value == null) return null;
                const command = monthIdx === 0 || row.values[monthIdx - 1] == null ? 'M' : 'L';
                return `${command} ${getX(monthIdx)} ${getY(value)}`;
              })
              .filter(Boolean)
              .join(' ');

            return (
              <g key={row.year}>
                <path
                  d={path}
                  fill="none"
                  stroke={COLORS[rowIdx % COLORS.length]}
                  strokeOpacity={getYearOpacity(row.year)}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {row.values.map((value, monthIdx) => {
                  if (value == null) return null;
                  const x = getX(monthIdx);
                  const y = getY(value);
                  return (
                    <circle
                      key={`${row.year}-${monthIdx}`}
                      cx={x}
                      cy={y}
                      r={POINT_RADIUS}
                      fill="#fff"
                      stroke={COLORS[rowIdx % COLORS.length]}
                      strokeOpacity={getYearOpacity(row.year)}
                      strokeWidth="2.5"
                      tabIndex={0}
                      aria-label={`${MONTH_LABELS[monthIdx]} ${row.year}: ${value.toLocaleString()} inquiries`}
                      onMouseEnter={() => showTooltip(x, y, `${MONTH_LABELS[monthIdx]} ${row.year}`, value)}
                      onMouseMove={() => showTooltip(x, y, `${MONTH_LABELS[monthIdx]} ${row.year}`, value)}
                      onFocus={() => showTooltip(x, y, `${MONTH_LABELS[monthIdx]} ${row.year}`, value)}
                      onBlur={() => setTooltip((prev) => ({ ...prev, show: false }))}
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>

        {tooltip.show && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-md text-slate-700"
            style={{
              left: `${Math.min(Math.max(tooltip.x, 60), chartWidth - 60)}px`,
              top: `${Math.max(tooltip.y - 8, 18)}px`
            }}
          >
            <div className="font-semibold text-slate-900">{tooltip.label}</div>
            <div className="mt-1 text-slate-600 tabular-nums">
              {tooltip.value.toLocaleString()} inquiries
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InquiryYearLineChart;
